import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { setUser as sentrySetUser } from '@sentry/browser';

import {
  firebaseSignInWithGoogle,
  firebaseSignInWithEmailAndPassword,
  firebaseResetPassword,
  firebaseUpdatePassword,
  firebaseReauthenticateUser,
} from '@/utils/firebase';
import { fetchUserinfo } from '@/queries/auth/fetchUserinfo';
import type { ExtendedUserCredential } from '@/types/auth';

/**
 * Identity now comes from the Envoy session (`/auth/userinfo`), not Firebase.
 * The query is shared (same key) with `AuthGateway`, which gates logged-in vs
 * logged-out. `signOut` is a full-page nav to Envoy's `/auth/signout`.
 *
 * The Firebase sign-in/password helpers remain only until the login/password UI
 * is removed; `AuthGateway` bypasses them (login happens via `/auth/signin`).
 */
export const useAuthentication = () => {
  const {
    data: user = null,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['auth', 'userinfo'],
    queryFn: fetchUserinfo,
    retry: false,
    staleTime: Infinity,
  });

  const signInWithGoogle = useCallback(() => {
    return firebaseSignInWithGoogle();
  }, []);

  const signInWithEmailAndPassword = useCallback(async (email: string, password: string) => {
    const results = await firebaseSignInWithEmailAndPassword(email, password);

    return results as ExtendedUserCredential;
  }, []);

  const signOut = useCallback(() => {
    sentrySetUser(null);
    window.location.assign('/auth/signout');
  }, []);

  const resetPassword = useCallback((code: string, newPassword: string) => {
    return firebaseResetPassword(code, newPassword);
  }, []);

  const changePassword = useCallback((newPassword: string) => {
    return firebaseUpdatePassword(newPassword);
  }, []);

  const reauthenticateUser = useCallback((password: string) => {
    return firebaseReauthenticateUser(password);
  }, []);

  return {
    user,
    signInWithGoogle,
    signInWithEmailAndPassword,
    signOut,
    loading,
    error,
    resetPassword,
    changePassword,
    reauthenticateUser,
  };
};
